import { useEffect, useId, useRef, useState } from 'react';
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  updateProfile,
} from 'firebase/auth';
import { auth } from '../firebase';
import { track } from '../lib/analytics';
import { ensureUserProfile } from '../lib/auth';
import Brand from './Brand';
import GoogleIcon from './GoogleIcon';
import Icon from './Icon';

export default function AuthModal({
  open,
  onClose,
  initialMode = 'signin',
  dismissible = true,
}) {
  const titleId = useId();
  const [mode, setMode] = useState(initialMode);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    if (open) {
      setMode(initialMode);
      setError('');
      setInfo('');
      setShowPassword(false);
    }
  }, [open, initialMode]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && dismissible) onClose?.();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    panelRef.current?.querySelector('input,button')?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose, dismissible]);

  if (!open) return null;

  const run = async (fn) => {
    setBusy(true);
    setError('');
    setInfo('');
    try {
      await fn();
      onClose?.();
    } catch (err) {
      setError((err.message || 'Something went wrong').replace('Firebase: ', ''));
    } finally {
      setBusy(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (mode === 'signup') {
      if (!name.trim()) return setError('Enter your name.');
      if (password !== confirm) return setError('Passwords do not match.');
      if (password.length < 6) return setError('Password must be at least 6 characters.');
    }
    run(async () => {
      if (mode === 'signup') {
        const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
        await updateProfile(cred.user, { displayName: name.trim() });
        await ensureUserProfile(cred.user, { displayName: name.trim() });
        await sendEmailVerification(cred.user);
        track('sign_up', { method: 'password' });
      } else {
        const cred = await signInWithEmailAndPassword(auth, email.trim(), password);
        await ensureUserProfile(cred.user);
        track('login', { method: 'password' });
      }
    });
  };

  const google = () =>
    run(async () => {
      const cred = await signInWithPopup(auth, new GoogleAuthProvider());
      await ensureUserProfile(cred.user, {
        displayName: cred.user.displayName || '',
      });
      track('login', { method: 'google' });
    });

  const reset = () =>
    run(async () => {
      if (!email.trim()) throw new Error('Enter your email to reset your password.');
      await sendPasswordResetEmail(auth, email.trim());
      setInfo('Password reset email sent.');
    });

  return (
    <div className="auth-modal-root" role="presentation">
      <button
        type="button"
        className="auth-modal-backdrop"
        aria-label={dismissible ? 'Close sign in' : 'Sign in required'}
        onClick={() => dismissible && onClose?.()}
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.78), rgba(0, 0, 0, 0.78)), url(${process.env.PUBLIC_URL}/login-bg.png)`,
        }}
      />
      <div
        className="auth-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={panelRef}
      >
        {dismissible && (
          <button type="button" className="auth-modal-close" onClick={onClose} aria-label="Close">
            <Icon name="close" size={18} />
          </button>
        )}

        <div className="auth-modal-body">
          <Brand to="/" />

          <div className="auth-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'signin'}
              className={mode === 'signin' ? 'active' : ''}
              onClick={() => setMode('signin')}
            >
              Sign in
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'signup'}
              className={mode === 'signup' ? 'active' : ''}
              onClick={() => setMode('signup')}
            >
              Sign up
            </button>
          </div>

          <h2 id={titleId}>{mode === 'signup' ? 'Create your account' : 'Welcome back'}</h2>
          <p className="auth-sub">
            {mode === 'signup'
              ? 'Join to browse, save, and message nearby.'
              : 'Sign in to browse listings near you.'}
          </p>

          <button type="button" className="google-button" onClick={google} disabled={busy}>
            <GoogleIcon size={20} />
            Continue with Google
          </button>

          <div className="auth-divider">
            <span>or</span>
          </div>

          <form onSubmit={submit} className="auth-form">
            {mode === 'signup' && (
              <label className="field">
                <input
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Name"
                  aria-label="Name"
                  autoComplete="name"
                />
              </label>
            )}
            <label className="field">
              <input
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                aria-label="Email"
                autoComplete="email"
              />
            </label>
            <div className="field field--password">
              <input
                required
                minLength={6}
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                aria-label="Password"
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              />
              <button
                type="button"
                className="field-toggle"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
              >
                <Icon name={showPassword ? 'eyeOff' : 'eye'} size={18} />
              </button>
            </div>
            {mode === 'signup' && (
              <div className="field field--password">
                <input
                  required
                  minLength={6}
                  type={showPassword ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Confirm password"
                  aria-label="Confirm password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="field-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                >
                  <Icon name={showPassword ? 'eyeOff' : 'eye'} size={18} />
                </button>
              </div>
            )}
            {error && <p className="form-error">{error}</p>}
            {info && <p className="form-info">{info}</p>}
            <button className="primary-button full" type="submit" disabled={busy}>
              {busy ? 'Please wait…' : mode === 'signup' ? 'Create account' : 'Sign in'}
            </button>
          </form>

          {mode === 'signin' && (
            <button type="button" className="text-button" onClick={reset} disabled={busy}>
              Forgot password?
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
